import { fetchChatGreeting } from "@/modules/chat/chat-greeting.api";
import { useStartVoiceCall } from "@/modules/chat/voice-call/hooks/use-start-voice-call";
import { useIsStaff } from "@/lib/auth/auth.roles";
import { SidebarTrigger } from "@/lib/components/sidebar/sidebar";
import { IconButton } from "@/lib/components/ui/button/button";
import Menu from "@/lib/components/ui/icons/menu";
import Search from "@/lib/components/ui/icons/search";
import Settings from "@/lib/components/ui/icons/settings";
import SquarePen from "@/lib/components/ui/icons/square-pen";
import X from "@/lib/components/ui/icons/x";
import InputGroup, {
  InputGroupAddon,
  InputGroupIconButton,
  InputGroupInput,
} from "@/lib/components/ui/input-group/input-group";
import KeyboardShortcut, {
  formatKeys,
  KeyboardShortcutContent,
  KeyboardShortcutTrigger,
  type KeyName,
} from "@/lib/components/ui/keyboard-shortcut/keyboard-shortcut";
import Spinner from "@/lib/components/ui/spinner";
import { cn } from "@/lib/utils";
import { useAuth } from "@frontegg/react";
import { PROCESS_ID } from "@soulbyte/core";
import { AudioLines } from "lucide-react";
import { useEffect, type HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { isReadOnlyChannel } from "../thread-source";
import { useCreateThread, useThread } from "../threads.api";
import { useThreadsContext } from "../threads.context";

export default function ThreadsHeader({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  const { t } = useTranslation();

  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const threadId = searchParams.get("threadId");

  const {
    assistant,
    isSearching,
    setIsSearching,
    searchQuery,
    setSearchQuery,
  } = useThreadsContext();

  // ===========================================================================
  // Search thread
  // ===========================================================================
  const searchShortcutKeys: KeyName[] = ["Shift", "Meta", "K"];

  useEffect(() => {
    if (!isSearching) return;

    // Close search when Escape is pressed
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setIsSearching(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isSearching, setIsSearching]);

  // ===========================================================================
  // Create thread
  // ===========================================================================
  const createShortcutKeys: KeyName[] = ["Shift", "Meta", "O"];

  const createThread = useCreateThread({
    onSuccess: async (thread) => {
      navigate(`/chat?assistantId=${assistant.id}&threadId=${thread.id}`);
    },
  });

  const { startVoiceCall, isStartingVoiceCall } = useStartVoiceCall();
  const isPending = createThread.isPending || isStartingVoiceCall;

  // voice calls are staff-only for now
  const isStaff = useIsStaff();

  // Website (widget) and phone conversations are read-only to staff: the
  // backend refuses voice.start on a thread the viewer can't post to
  // (THREAD_READ_ONLY), so offering the call button there only sets up a
  // server-side failure. Hide it while such a conversation is open.
  //
  // A FAILED lookup hides it too. `thread` is undefined on a 403/404, so
  // isReadOnlyChannel(undefined) is false and the button used to appear on
  // exactly the threads the backend had just refused — the same defect already
  // fixed for the composer in chat.tsx, which was not propagated here. When we
  // cannot establish that a call is allowed, do not offer one.
  const { thread, error: threadError } = useThread({
    assistantId: assistant.id ?? "",
    threadId: threadId ?? "",
  });
  const threadUnresolved =
    Boolean(threadId) && (Boolean(threadError) || !thread);
  const canCall =
    isStaff && !threadUnresolved && !isReadOnlyChannel(thread?.source);

  async function handleCreateThread() {
    if (!user?.id || !assistant.id) return;
    if (isPending || !createThread.isEntitled) return;

    // This flow creates the thread EAGERLY (unlike the placeholder's lazy
    // first-submit creation), so resolve the assistant's chat greeting first
    // and pin it via greeting_id — the backend persists it as the thread's
    // opening assistant message, and the conversation view opens showing it
    // (with its quick replies tappable through the existing widget
    // rehydration). Best-effort: a greeting is optional copy and must never
    // block creating the thread.
    const greeting = await fetchChatGreeting(
      assistant.id,
      user.accessToken,
    ).catch(() => null);

    // greeting_id is not yet in the generated CreateThreadRequestDTO (types
    // regenerate from the deployed swagger); the intermediate variable avoids
    // excess-property checking until then — see chat-greeting.api.ts.
    const body = {
      user_id: user.id,
      title: "New Chat",
      stage: "draft",
      process_id: PROCESS_ID,
      ...(greeting ? { greeting_id: greeting.id } : {}),
    };
    createThread.mutate({
      params: {
        path: {
          assistant_id: assistant.id!,
        },
      },
      body,
    });
  }

  function handleCloseSearch() {
    setIsSearching(false);
    setSearchQuery("");
  }

  if (isSearching) {
    return (
      <header
        className={cn("h-topbar flex items-center gap-3 px-3", className)}
        {...props}
      >
        <InputGroup size="sm" className="flex-1">
          <InputGroupAddon>
            <Search />
          </InputGroupAddon>
          <InputGroupInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
            placeholder={t("threads_search_placeholder")}
          />
          <InputGroupAddon align="inline-end">
            <InputGroupIconButton size="xs" onClick={handleCloseSearch}>
              <X />
            </InputGroupIconButton>
          </InputGroupAddon>
        </InputGroup>
      </header>
    );
  }

  return (
    <header
      className={cn(
        "h-topbar flex items-center justify-between gap-4 py-2 pr-3 pl-5",
        className,
      )}
      {...props}
    >
      <IconButton
        size="lg"
        variant="ghost"
        aria-label={t("assistants_settings_label")}
        disabled={!assistant.id}
        onClick={() => {
          const moduleId = assistant.id;
          if (!moduleId) return;
          // open the module settings dialog above the chat instead of
          // navigating away
          setSearchParams((prev) => {
            prev.set("moduleId", moduleId);
            return prev;
          });
        }}
      >
        <Settings />
      </IconButton>
      <div className="flex">
        <SidebarTrigger asChild>
          <IconButton size="lg" variant="ghost" className="md:hidden">
            <Menu />
          </IconButton>
        </SidebarTrigger>
        <KeyboardShortcut
          keys={searchShortcutKeys}
          onTrigger={() => setIsSearching(true)}
        >
          <KeyboardShortcutTrigger asChild>
            <IconButton
              size="lg"
              onClick={() => setIsSearching(true)}
              variant="ghost"
              className="hidden md:flex"
            >
              <Search />
            </IconButton>
          </KeyboardShortcutTrigger>
          <KeyboardShortcutContent>
            {t("threads_search_placeholder")} ({formatKeys(searchShortcutKeys)})
          </KeyboardShortcutContent>
        </KeyboardShortcut>
        {canCall && (Boolean(threadId) || createThread.isEntitled) && (
          <IconButton
            size="lg"
            onClick={() => {
              if (!assistant.id) return;
              void startVoiceCall({
                assistantId: assistant.id,
                threadId: threadId,
              });
            }}
            variant="ghost"
            disabled={isStartingVoiceCall}
            className="hidden md:flex"
            aria-label={t("voice_call_aria_label")}
          >
            {isStartingVoiceCall ? (
              <Spinner />
            ) : (
              <AudioLines className="size-5" strokeWidth={1.3} />
            )}
          </IconButton>
        )}
        {createThread.isEntitled && (
          <KeyboardShortcut
            keys={createShortcutKeys}
            onTrigger={handleCreateThread}
          >
            <KeyboardShortcutTrigger asChild>
              <IconButton
                size="lg"
                onClick={handleCreateThread}
                variant="ghost"
                disabled={createThread.isPending}
                className="hidden md:flex"
              >
                {createThread.isPending ? <Spinner /> : <SquarePen />}
              </IconButton>
            </KeyboardShortcutTrigger>
            <KeyboardShortcutContent>
              {t("threads_new_chat")} ({formatKeys(createShortcutKeys)})
            </KeyboardShortcutContent>
          </KeyboardShortcut>
        )}
      </div>
    </header>
  );
}
